export function toRawString(amountInWei, decimals) {
    const factor = BigInt(10 ** decimals);
    // Calculate the whole and fractional parts
    const wholePart = amountInWei / factor;
    const fractionalPart = amountInWei % factor;
    // Convert whole part to string
    const wholeStr = wholePart.toString();
    // Convert fractional part to string with leading zeros to maintain decimal precision
    let fractionalStr = fractionalPart.toString().padStart(decimals, "0");
    // Trim trailing zeros in the fractional part
    fractionalStr = fractionalStr.replace(/0+$/, "");
    // If there's no fractional part after trimming, return only the whole part
    return fractionalStr ? `${wholeStr}.${fractionalStr}` : wholeStr;
}
export function divideBigIntToString(numerator, denominator, decimalPlaces) {
    const integerPart = numerator / denominator;
    // Calculate the remainder and use it to find decimal places
    let remainder = numerator % denominator;
    let decimalStr = "";
    for (let i = 0; i < decimalPlaces; i++) {
        remainder *= BigInt(10);
        const digit = remainder / denominator;
        decimalStr += digit.toString();
        remainder %= denominator;
    }
    return `${integerPart.toString()}.${decimalStr}`;
}
export function formatTokenAmount(amountInWei, decimals, significantFigures = 6, showSubscripts = true) {
    return formatDisplayNumber(formatUnits(amountInWei.toString(), decimals), {
        significantDigits: significantFigures,
        showSubscripts: showSubscripts,
    });
}
const subscriptMap = {
    "0": "₀",
    "1": "₁",
    "2": "₂",
    "3": "₃",
    "4": "₄",
    "5": "₅",
    "6": "₆",
    "7": "₇",
    "8": "₈",
    "9": "₉",
};
export const formatDisplayNumber = (value, options) => {
    const { style = "decimal", fallback = "--", showSubscripts = true } = options || {};
    // Ensure significantDigits is within valid range (1-21) for Intl.NumberFormat
    let significantDigits = style === "decimal" ? options?.significantDigits || 6 : options?.significantDigits;
    if (significantDigits && (significantDigits < 1 || significantDigits > 21)) {
        significantDigits = Math.min(Math.max(significantDigits, 1), 21);
    }
    const fractionDigits = style === "currency" ? options?.fractionDigits || 2 : options?.fractionDigits;
    const currency = style === "currency" ? "$" : "";
    const percent = style === "percent" ? "%" : "";
    const v = Number(value?.toString());
    if (value === undefined || value === null || Number.isNaN(value))
        return fallback;
    if (v < 1) {
        const decimal = value.toString().split(".")[1] || "0";
        const numberOfLeadingZeros = -Math.floor(Math.log10(v) + 1);
        const slicedDecimal = decimal
            .replace(/^0+/, "")
            .slice(0, significantDigits ? significantDigits : 30)
            .slice(0, fractionDigits ? fractionDigits : 30)
            .replace(/0+$/, "");
        if (showSubscripts && numberOfLeadingZeros > 3) {
            const subscripts = numberOfLeadingZeros
                .toString()
                .split("")
                .map(item => subscriptMap[item])
                .join("");
            return `${currency}0.0${subscripts}${slicedDecimal}${percent}`;
        }
        return `${currency}0${slicedDecimal.length ? "." + "0".repeat(numberOfLeadingZeros) + slicedDecimal : ""}${percent}`;
    }
    const formatter = Intl.NumberFormat("en-US", {
        notation: v >= 10000000 ? "compact" : "standard",
        style,
        currency: "USD",
        minimumFractionDigits: fractionDigits ? 0 : undefined,
        maximumFractionDigits: fractionDigits,
        minimumSignificantDigits: significantDigits ? 1 : undefined,
        maximumSignificantDigits: significantDigits,
    });
    return formatter.format(v);
};
/**
 * Safely convert a string to BigInt, returning 0n for invalid values.
 * Use this for consumer-provided amount strings that may not be valid wei values.
 */
export function safeBigInt(value) {
    try {
        return BigInt(value);
    }
    catch {
        return BigInt(0);
    }
}
export function formatUnits(value, decimals = 18) {
    const factor = BigInt(10) ** BigInt(decimals);
    const wholePart = BigInt(value) / factor;
    const fractionalPart = BigInt(value) % factor;
    // Convert fractional part to string and pad with leading zeros
    const fractionalStr = fractionalPart.toString().padStart(Number(decimals), "0");
    // Remove trailing zeros in the fractional part
    const formattedFractionalStr = fractionalStr.replace(/0+$/, "");
    // If no fractional part, return only the whole part
    if (formattedFractionalStr === "") {
        return wholePart.toString();
    }
    // Otherwise, combine whole and fractional parts
    return `${wholePart.toString()}.${formattedFractionalStr}`;
}
